import { useState } from "react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { Reveal, SectionHeader } from "./primitives";

const R = 132;
const C = 2 * Math.PI * R;

const Tokenomics = () => {
  const config = useSiteConfig();
  const [active, setActive] = useState(null);
  const allocs = config.allocations;
  const current = active !== null ? allocs[active] : null;

  let offset = 0;
  const segments = allocs.map((a) => {
    const len = (a.percent / 100) * C;
    const seg = { ...a, len, dashOffset: -offset };
    offset += len;
    return seg;
  });

  return (
    <section id="tokenomics" className="relative py-24 lg:py-36 border-t border-line" data-testid="tokenomics-section">
      <div className="section-shell">
        <SectionHeader
          index={4}
          eyebrow="Tokenomics"
          title={<>One supply.<br />Six commitments.</>}
          sub="Every JCV is accounted for. Allocations total exactly 100% of the fixed maximum supply — 72,000,000,000,000 JCV."
          testId="tokenomics-header"
        />

        <div className="mt-14 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal className="flex justify-center" testId="tokenomics-chart-wrap">
            <div className="relative w-full max-w-[420px]">
              <svg viewBox="0 0 400 400" className="w-full h-auto" role="img" aria-label="JCV allocation donut chart">
                <g transform="rotate(-90 200 200)">
                  {segments.map((s, i) => (
                    <circle
                      key={s.id}
                      cx="200" cy="200" r={R}
                      fill="none"
                      stroke={s.color}
                      strokeWidth={active === i ? 44 : 32}
                      strokeDasharray={`${s.len - 3} ${C - s.len + 3}`}
                      strokeDashoffset={s.dashOffset}
                      opacity={active === null || active === i ? 1 : 0.25}
                      className="cursor-pointer transition-[stroke-width,opacity] duration-300"
                      tabIndex={0}
                      role="button"
                      aria-label={`${s.name}: ${s.percent} percent, ${s.amount} JCV`}
                      data-testid={`chart-segment-${s.id}`}
                      onMouseEnter={() => setActive(i)}
                      onMouseLeave={() => setActive(null)}
                      onFocus={() => setActive(i)}
                      onBlur={() => setActive(null)}
                      onClick={() => setActive(active === i ? null : i)}
                    />
                  ))}
                </g>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none px-16">
                {current ? (
                  <>
                    <div className="font-mono text-2xl sm:text-3xl font-semibold" style={{ color: current.color }} data-testid="chart-center-percent">
                      {current.percent}%
                    </div>
                    <div className="mt-2 font-mono text-[10px] sm:text-xs text-gold break-all leading-relaxed" data-testid="chart-center-amount">
                      {current.amount} JCV
                    </div>
                    <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-slate max-w-[160px] leading-snug">
                      {current.name}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="gold-number font-semibold text-[13px] sm:text-base leading-relaxed break-all" data-testid="chart-center-supply">
                      {config.token.maxSupply}
                    </div>
                    <div className="mt-2 font-mono text-[9px] sm:text-[11px] tracking-[0.25em] uppercase text-slate">JCV Total Supply</div>
                  </>
                )}
              </div>
            </div>
          </Reveal>

          <div className="space-y-3">
            {allocs.map((a, i) => (
              <Reveal key={a.id} delay={i * 0.06} testId={`allocation-row-${a.id}`}>
                <button
                  className={`card-surface w-full rounded-lg px-5 sm:px-6 py-4 flex items-center gap-4 text-left ${
                    active === i ? "border-teal/60" : ""
                  }`}
                  onMouseEnter={() => setActive(i)}
                  onMouseLeave={() => setActive(null)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                  onClick={() => setActive(active === i ? null : i)}
                  data-testid={`allocation-button-${a.id}`}
                >
                  <span className="h-3.5 w-3.5 rounded-sm shrink-0" style={{ backgroundColor: a.color }} aria-hidden="true" />
                  <span className="flex-1 min-w-0">
                    <span className="block font-display font-semibold text-sm sm:text-base text-mist truncate">{a.name}</span>
                    <span className="block font-mono text-[11px] sm:text-xs text-slate mt-1">{a.amount} JCV</span>
                  </span>
                  <span className="font-mono text-lg sm:text-xl font-semibold" style={{ color: a.color }}>
                    {a.percent}%
                  </span>
                </button>
              </Reveal>
            ))}
            <Reveal delay={0.4}>
              <div className="flex items-center justify-between rounded-lg border border-gold/40 bg-gold/5 px-5 sm:px-6 py-4" data-testid="allocation-total">
                <span className="font-mono text-xs tracking-[0.25em] uppercase text-gold">Total</span>
                <span className="font-mono text-sm sm:text-base text-gold font-semibold">100% — 72,000,000,000,000 JCV</span>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tokenomics;
